import { useLogger } from '@shared/logger';
import { isRestrictedEmailDomain } from '@shared/utils';
import { useConfig } from '@user-management/configuration';
import {
  GetUserAppRolesDocument,
  GetUserProfileDocument,
  GetUserStatusDocument,
  useUpdateUserAppRoleByNamesMutation,
  useUpdateUserMutation,
  useUserUpdateStatusMutation,
} from '@user-management/graphql';
import { useCallback, useMemo } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { UserStatus } from '../../constants';
import {
  type UseUserDetailsFormSubmissionError,
  type UserDetailsFormValues,
  type UseUserDetailsFormSubmissionErrorType,
  type User,
} from '../../types';

interface UseUserDetailsFormSubmissionParams {
  formContext: UseFormReturn<UserDetailsFormValues>;
  user?: User;
  onCompleted?: () => void;
  onError?: () => void;
}

interface ErrorExtension {
  response: {
    body: {
      error: {
        code: string;
      };
    };
  };
}

export default function useUserDetailsFormSubmission(params: UseUserDetailsFormSubmissionParams) {
  const { user, onCompleted, onError, formContext } = params;

  const userId = user?.id ?? '';

  const {
    userManagement: { restrictedUserEmailDomains },
  } = useConfig();

  const isRestrictedEmail = isRestrictedEmailDomain(user?.email ?? '', restrictedUserEmailDomains);

  const logger = useLogger('useUserDetailsFormSubmission');
  const [updateUser, { loading: isUpdateUserLoading, error: updateUserError }] =
    useUpdateUserMutation();

  const [updateUserStatus, { loading: isUpdateUserStatusLoading, error: updateUserStatusError }] =
    useUserUpdateStatusMutation();

  const [
    updateUserAppRoleByNames,
    { loading: isUpdateUserAppRoleByNamesLoading, error: updateUserAppRoleByNamesError },
  ] = useUpdateUserAppRoleByNamesMutation();

  const errors = useMemo<UseUserDetailsFormSubmissionError[]>(() => {
    const result: UseUserDetailsFormSubmissionError[] = [];
    if (updateUserError) {
      result.push({ type: 'USER_PROFILE', error: updateUserError });
    }
    if (updateUserStatusError) {
      result.push({ type: 'USER_STATUS', error: updateUserStatusError });
    }
    if (updateUserAppRoleByNamesError) {
      result.push({ type: 'USER_APP_ROLES', error: updateUserAppRoleByNamesError });
    }
    return result;
  }, [updateUserError, updateUserStatusError, updateUserAppRoleByNamesError]);

  const handleSubmit = useCallback(
    async (formValues: UserDetailsFormValues) => {
      if (!user) return;

      const handleUpdateUserStatus = async () => {
        if (formValues.activated !== undefined) {
          await updateUserStatus({
            variables: {
              data: {
                userId,
                status: formValues.activated === 'true' ? UserStatus.ENABLED : UserStatus.DISABLED,
              },
            },
            onCompleted: (data) => {
              if (!data.suUpdateUserStatus.success) {
                throw new Error('Failed to update user status');
              }
            },
            refetchQueries: [
              {
                query: GetUserStatusDocument,
                variables: {
                  userId,
                },
              },
            ],
            awaitRefetchQueries: true,
          });
        }
      };

      const handleUpdateUser = async () => {
        await updateUser({
          variables: {
            userId,
            data: {
              firstname: isRestrictedEmail ? user.firstName : formValues.firstName,
              lastname: isRestrictedEmail ? user.lastName : formValues.lastName,
            },
          },
          onError: (error) => {
            const errorExtensions = error.cause?.extensions;
            if (!errorExtensions) {
              throw new Error('GraphQL error extensions not found');
            } else if (Array.isArray(errorExtensions)) {
              for (const error of errorExtensions) {
                logger.error('GraphQL error extension:', error);
              }
              throw new Error('GraphQL error extensions found');
            }
            const errorCode = (errorExtensions as unknown as ErrorExtension).response.body.error
              .code;

            if (errorCode === 'ER_DUP_ENTRY') {
              formContext.setError('email', {
                type: 'manual',
                message: 'อีเมลนี้มีอยู่ในระบบแล้ว กรุณาตรวจสอบหรือใช้ที่อยู่อีเมลอื่น',
              });
            }
            throw new Error('Failed to update user profile');
          },
          refetchQueries: [
            {
              query: GetUserProfileDocument,
              variables: {
                userId,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
      };

      const handleUpdateUserAppRoleByNames = async () => {
        await updateUserAppRoleByNames({
          variables: {
            data: {
              userId,
              appRoleNames: formValues.appRoles?.map((role) => role.label) ?? [],
            },
          },
          refetchQueries: [
            {
              query: GetUserAppRolesDocument,
              variables: {
                userId,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
      };

      const promiseResults = await Promise.allSettled([
        handleUpdateUser(),
        handleUpdateUserStatus(),
        handleUpdateUserAppRoleByNames(),
      ]);

      // NOTE: Map operation names to their corresponding results
      // This array order should always match the order of the promise results array above
      const operationNames: UseUserDetailsFormSubmissionErrorType[] = [
        'USER_PROFILE',
        'USER_STATUS',
        'USER_APP_ROLES',
      ];

      const failedOperations = promiseResults
        .map((result, index) => {
          if (result.status === 'rejected') {
            return {
              operation: operationNames[index],
              error: result.reason instanceof Error ? result.reason.message : String(result.reason),
            };
          }
          return null;
        })
        .filter(
          (op): op is { operation: UseUserDetailsFormSubmissionErrorType; error: string } =>
            op !== null,
        );

      if (failedOperations.length > 0) {
        onError?.();

        const errorDetails = failedOperations
          .map((op) => `${op.operation}: ${op.error}`)
          .join(', ');
        logger.error('Form submission failed', errorDetails);
        throw new Error(`Form submission failed: ${errorDetails}`);
      }

      onCompleted?.();
    },
    [
      onCompleted,
      updateUserStatus,
      userId,
      updateUser,
      logger,
      formContext,
      updateUserAppRoleByNames,
      onError,
      isRestrictedEmail,
      user,
    ],
  );

  return {
    handleSubmit,
    loading: isUpdateUserLoading || isUpdateUserStatusLoading || isUpdateUserAppRoleByNamesLoading,
    errors,
  };
}
